/**
 * MenyTile — Middag-flisen på Hjem (Wave 3; minifliser i #154).
 *
 * The meal planner lives at /meny (Plan → Middag), so this tile teases today's
 * planned dinner (or the next upcoming one this week) and links there.
 * #154: én av de tre miniflisene; «Middag» er etiketten og retten verdien.
 * Allergenchipen fra skissen («Nøttefri») er utelatt: `Meal` har ingen
 * allergendata, og en skanning på klienten er ikke bestemt (MAPPING.md, Hjem).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeals } from '../../meals/useMeals';
import { MiniTile } from './MiniTile';

function todayYMD(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}
function addDays(ymd: string, n: number): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function mondayOf(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  return addDays(ymd, -((d.getUTCDay() + 6) % 7));
}

export function MenyTile(): ReactNode {
  const { t } = useTranslation();
  const today = todayYMD();
  const weekStart = mondayOf(today);
  const { data } = useMeals(weekStart, addDays(weekStart, 6));

  const meals = data?.meals ?? [];
  // Today's meal wins; otherwise the next upcoming one this week (listRange is date-asc).
  const todayMeal = meals.find((m) => m.date === today);
  const upcoming = meals.find((m) => m.date && m.date >= today);
  const value = todayMeal
    ? todayMeal.title
    : upcoming
      ? t('meny.tile.next', { title: upcoming.title })
      : t('home.dinner.empty');

  return (
    <MiniTile
      to="/meny"
      label={t('nav.meal')}
      data-testid="meny-tile"
      labelTestId="meny-tile-headline"
      valueTestId="meny-tile-subline"
    >
      {value}
    </MiniTile>
  );
}
